import { generatePath, matchPath } from 'react-router';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { NEW_ENTITY_ID } from 'constants/EntityTitles';
import { PATH } from 'constants/RoutesConstants';
import { putAndWait } from 'modules/app/sagas';
import { scopeToPage } from 'modules/app/sagas/utils';
import { selectDealer } from 'modules/dealers/actions';
import { isApiFailure } from 'modules/global/generic.guards';
import type { PutAndWaitResult } from 'modules/global/generic.types';
import { selectGroup } from 'modules/groups/actions';
import * as actions from 'modules/guidelines/actions';
import * as actionTypes from 'modules/guidelines/actionTypes';
import type { Guideline } from 'modules/guidelines/types';
import { navigate } from 'modules/router/navigate';
import { getCurrentLocation, getLocationState } from 'selectors/router';

import type { PostGuidelineSuccessAction } from '../types/actions';

// Guideline creation is dispatched from the `/new` details page; on success, redirect
// to the saved guideline's URL (preserving router state).
function* afterPostGuideline({ payload: guideline }: PostGuidelineSuccessAction) {
  const state: unknown = yield select(getLocationState);
  const location: ReturnType<typeof getCurrentLocation> = yield select(getCurrentLocation);
  const isGroup = matchPath(PATH.GROUP_GUIDELINE_DETAILED.path, location) !== null;
  const detailPath = isGroup ? PATH.GROUP_GUIDELINE_DETAILED : PATH.GUIDELINE_DETAILED;
  yield call(navigate, generatePath(detailPath.path, { guidelineId: guideline.guidelineId }), { replace: true, state });
}

// Entering a guideline details page: load the guideline, then sync the selected
// dealer/group from the response. `scopeToPage` already gates on APPLICATION_IS_READY.
function* onDetailsPage() {
  // Set up the post-create redirect first so it also covers the `/new` case below.
  yield takeLatest(actionTypes.POST_GUIDELINE_SUCCESS, afterPostGuideline);

  const location: ReturnType<typeof getCurrentLocation> = yield select(getCurrentLocation);
  const match =
    matchPath(PATH.GUIDELINE_DETAILED.path, location) ??
    matchPath(PATH.GROUP_GUIDELINE_DETAILED.path, location);
  const guidelineId = match?.params?.guidelineId;

  if (!guidelineId || guidelineId === NEW_ENTITY_ID) {
    return;
  }

  const response: PutAndWaitResult<Guideline> = yield putAndWait(actions.fetchGuideline(guidelineId));
  if (!isApiFailure(response)) {
    yield put(actions.selectGuideline(guidelineId));
    const { dealerId, groupId } = response.payload;
    if (dealerId) {
      yield put(selectDealer(dealerId));
    }
    if (groupId) {
      yield put(selectGroup(groupId));
    }
  }
}

export default function* detailsSaga() {
  yield scopeToPage([PATH.GUIDELINE_DETAILED, PATH.GROUP_GUIDELINE_DETAILED], onDetailsPage);
}
